import React, { useState, useEffect, Fragment } from "react";
import { useSelector } from "react-redux";

import { FormattedNumber } from "../FormattedNumber/FormattedNumber";
import { getSavedPersonInfo } from "../../reducers/selectors";
import { showToast } from "../../utils/helper";
import { getBankAccountBalance } from "../../services/api/accounts";
import { Icons } from "../UI/Icon/Icon";
import { CircularProgress } from "../UI/CircularProgress/CircularProgress";

import style from "./BankAccountsSelector.scss";
import { modes } from "../Invest/FundsTable/FundsAssetTable/FundsAssetTable";
import Currency from "bx-react-ui/src/components/Asset/Tabs/Currency/Currency";

const defaultFilters = {
  currencyISO: "",
  amount: 0,
};

export const BankAccountsSelector = ({
  bankAccounts,
  setAllBankAccountAreInvalid = () => {},
  setLoadingStates = () => {},
  ...props
}) => {
  const [validBankAccounts, setValidBankAccounts] = useState({});
  const [loadingAccounts, setloadingAccounts] = useState(
    bankAccounts.reduce((acc, account) => {
      if (account.PersonBankAccountStatus === "Active") {
        acc[account.PersonBankAccountNumber] = true;
      }
      return acc;
    }, {})
  );
  const [currency, setCurrency] = useState({});

  useEffect(() => {
    let allInvalid = true;
    let notEnoughFunds = false;
    //if (props.filters.amount == 0) allInvalid = false;
    Object.values(validBankAccounts).forEach((key) => {
      if (key) {
        allInvalid = false;
      } else {
        if (!currency) notEnoughFunds = true;
      }
    });
    setAllBankAccountAreInvalid(allInvalid, notEnoughFunds);
  }, [bankAccounts, validBankAccounts, loadingAccounts]);

  const setBankAccountIsValid = (PersonBankAccountNumber, valid, currency) => {
    setValidBankAccounts((prev) => ({ ...prev, [PersonBankAccountNumber]: valid }));
    setCurrency(currency);
  };

  const setloadingBalances = (PersonBankAccountNumber, isLoading) => {
    setloadingAccounts((prev) => {
      const updated = { ...prev, [PersonBankAccountNumber]: isLoading };
      const allLoaded = Object.values(updated).every((val) => val === false);
      setLoadingStates(!allLoaded);
      return updated;
    });
  };

  return (
    <div className={style.wrapper}>
      {bankAccounts
        .filter((account) => account.PersonBankAccountStatus === "Active")
        .map((account, index) => (
          <BankAccountItem
            bankAccountItem={account}
            index={index}
            setloadingBalances={setloadingBalances}
            setBankAccountIsValid={setBankAccountIsValid}
            {...props}
          />
        ))}
    </div>
  );
};

const BankAccountItem = ({
  bankAccountItem,
  selectedBankAccount,
  onSelectBankAccount = () => {},
  filters = defaultFilters,
  disabled = false,
  setloadingBalances = () => {},
  loading,
  index,
  setBankAccountIsValid = () => {},
  setAnyError = () => {},
}) => {
  const [balance, setBalance] = useState(0);
  const [balanceResult, setBalanceResult] = useState(0);
  const [notEnoughFunds, setNotEnoughFunds] = useState(true);
  const [isCurrency, setIsCurrency] = useState(true);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const personInfo = useSelector(getSavedPersonInfo);

  useEffect(() => {
    setNotEnoughFunds(
      () =>
        (filters.mode === modes.BUY && filters.amount && +filters.amount > +balance) ||
        (filters.mode === modes.BUY && balance == 0)
    );
    setIsCurrency(
      () => filters.currencyISO && bankAccountItem.PersonBankAccountCurrencyISO !== filters.currencyISO
    );
  }, [filters, balance, bankAccountItem]);

  useEffect(() => {
    setBankAccountIsValid(
      bankAccountItem.PersonBankAccountNumber,
      !isCurrency && !notEnoughFunds,
      isCurrency
    );
  }, [bankAccountItem, isCurrency, notEnoughFunds]);

  useEffect(() => {
    if (bankAccountItem) getBankAccountBalanceAsync();
  }, [bankAccountItem]);

  useEffect(() => {
    setloadingBalances(bankAccountItem.PersonBankAccountNumber, loadingBalance);
  }, [loadingBalance]);

  const getBankAccountBalanceAsync = async () => {
    setLoadingBalance(() => true);

    try {
      const { response } = await getBankAccountBalance({
        bankAccountNumber: bankAccountItem.PersonBankAccountNumber,
      });
      if (response !== undefined) {
        setBalance(() => response.balance);
        setBalanceResult(() => response.result);
        if (response.result && response.result.Code !== 200) {
          setAnyError();
        }
      } else {
        setBalanceResult(() => ({
          Code: 400,
        }));
        setAnyError();
      }
    } catch (error) {
      setBalanceResult(() => ({
        Code: 400,
      }));
      console.error(error);
      setAnyError();
    }

    setLoadingBalance(() => false);
  };

  return (
    <div
      className={`${style.bankAccountItem} ${
        selectedBankAccount &&
        selectedBankAccount.PersonBankAccountNumber === bankAccountItem.PersonBankAccountNumber
          ? style.active
          : ""
      } ${notEnoughFunds || isCurrency || disabled ? style.disabled : null}`}
      key={index}
      onClick={() => {
        if (!loading && !isCurrency && !disabled && !notEnoughFunds) onSelectBankAccount(bankAccountItem);
      }}
    >
      <div className={style.row}>
        {bankAccountItem.PersonBankAccountName ? (
          <div className={style.bankAccountName}>{bankAccountItem.PersonBankAccountName}</div>
        ) : (
          <div>{`${personInfo.PhysicalPersonFirstName} ${personInfo.PhysicalPersonSecondName} ${personInfo.PhysicalPersonFirstLastName} ${personInfo.PhysicalPersonSecondLastName}`}</div>
        )}
        <span>
          {selectedBankAccount ? (
            selectedBankAccount.PersonBankAccountNumber === bankAccountItem.PersonBankAccountNumber ? (
              <Icons.RadioCheckedBlackIcon size="sm" />
            ) : (
              <Icons.RadioUncheckedBlackIcon size="sm" />
            )
          ) : (
            <Icons.RadioUncheckedBlackIcon size="sm" />
          )}
        </span>
      </div>
      <hr />
      <span
        className={`${style.row} ${style.balance} ${
          notEnoughFunds || isCurrency || disabled ? style.disabled : ""
        }`}
      >
        <p> Nº cuenta: {bankAccountItem.PersonBankAccountNumber}</p>
        <p>
          <span className={style.balanceLoader}>
            {`Saldo: `}
            {loadingBalance ? (
              <CircularProgress sm />
            ) : (
              <Fragment>
                {balanceResult && balanceResult.Code !== 200 ? (
                  <span className={style.balanceLoader} style={{ color: "red" }}>
                    No disponible
                  </span>
                ) : (
                  <Fragment>
                    <FormattedNumber
                      value={balance}
                      minimumFractionDigits={bankAccountItem.PersonBankAccountCurrencyISO == "USD" ? 2 : 0}
                      maximumFractionDigits={bankAccountItem.PersonBankAccountCurrencyISO == "USD" ? 2 : 0}
                    />
                  </Fragment>
                )}
                {` ${bankAccountItem.PersonBankAccountCurrencyISO}`}
              </Fragment>
            )}
          </span>
        </p>
      </span>
    </div>
  );
};
